import React, { useState } from 'react';
import { Check, ArrowRight, ShieldCheck } from 'lucide-react';
import { RANKR_MONTHLY, RANKR_ANNUAL_MONTHLY, RANKR_ANNUAL_DISCOUNT, RANKR_INCLUDED } from '@/data/rankr';
import { CURRENCIES } from '@/data/currencies';
import CurrencyToggle from '@/components/CurrencyToggle';

export default function RankrPricing() {
  const [annual, setAnnual] = useState(true);
  const [code, setCode] = useState('USD');
  const c = CURRENCIES.find((x) => x.code === code);
  const fmt = (usd) => `${c.symbol}${Math.round(usd * c.rate).toLocaleString('en-US')}`;
  const perMonth = annual ? RANKR_ANNUAL_MONTHLY : RANKR_MONTHLY;

  return (
    <section id="rankr-pricing" className="bg-[#0B0B0B] border-t border-white/10 py-16 lg:py-24">
      <div className="max-w-[1000px] mx-auto px-6 lg:px-10">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#3B82F6] text-center">Pricing</p>
        <h2 className="mt-4 font-display font-bold text-white text-3xl lg:text-[2.75rem] tracking-tight leading-tight text-center">
          One plan. Everything included.
        </h2>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <div className="inline-flex items-center gap-1 p-1 rounded-lg border border-white/10 bg-white/[0.03]">
            {[{ v: false, l: 'Monthly' }, { v: true, l: 'Yearly' }].map((o) => (
              <button key={o.l} type="button" onClick={() => setAnnual(o.v)}
                className={`px-5 h-9 rounded-md text-xs font-semibold transition-colors ${annual === o.v ? 'bg-[#2563EB] text-white' : 'text-white/50 hover:text-white'}`}>
                {o.l}
                {o.v && <span className="ml-2 text-[10px] text-emerald-300">Save {RANKR_ANNUAL_DISCOUNT * 100}%</span>}
              </button>
            ))}
          </div>
          <CurrencyToggle value={code} onChange={setCode} />
        </div>

        <div className="mt-10 rounded-2xl border-2 border-[#3B82F6] bg-gradient-to-b from-[#2563EB]/20 to-transparent blue-glow p-8 lg:p-10 grid lg:grid-cols-2 gap-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#3B82F6] font-semibold">Rankr Growth</p>
            <div className="mt-4 flex items-end gap-2">
              <span className="font-display font-bold text-white text-6xl">{fmt(perMonth)}</span>
              <span className="text-sm text-white/45 mb-2">/ month</span>
            </div>
            <p className="mt-2 text-xs text-white/45 h-4">
              {annual ? `Billed ${fmt(RANKR_ANNUAL_MONTHLY * 12)} yearly` : 'Billed monthly, cancel anytime'}
            </p>
            <a href="#rankr-start" className="group mt-8 w-full inline-flex items-center justify-center gap-2 h-14 rounded-lg text-sm font-bold bg-[#2563EB] text-white hover:bg-[#1D4ED8] shadow-lg shadow-[#2563EB]/40 transition-colors">
              Start Growing <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <p className="mt-5 flex items-start gap-2 text-xs text-white/60 leading-relaxed">
              <ShieldCheck className="w-4 h-4 text-[#3B82F6] shrink-0" />
              Iron-clad 90-day money-back guarantee. Not happy? Full refund, no questions.
            </p>
          </div>
          <ul className="space-y-3">
            {RANKR_INCLUDED.map((f) => (
              <li key={f} className="flex gap-2.5 text-sm text-white/80"><Check className="w-4 h-4 text-[#3B82F6] shrink-0 mt-0.5" />{f}</li>
            ))}
          </ul>
        </div>
        {code !== 'USD' && <p className="mt-4 text-xs text-white/35 text-center">Indicative conversion. All subscriptions are billed in USD.</p>}
      </div>
    </section>
  );
}